import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { catalog } from '../api/index.js';
import { Badge, statusBadge } from '../components/ui/badge.js';
import { Card } from '../components/ui/card.js';
import { Loader2, Search, AlertCircle, Layers } from 'lucide-react';
import { format } from 'date-fns';

const SENTINEL = 'ig.field.type.delete';

function imgUrl(v: unknown): string | undefined {
  if (!v || v === SENTINEL) return undefined;
  if (typeof v === 'string') return v || undefined;
  if (typeof v === 'object') {
    const o = v as any;
    return o.url ?? o.src ?? undefined;
  }
  return undefined;
}

function fmtDate(v: unknown): string {
  if (!v || typeof v !== 'string') return '—';
  try { return format(new Date(v), 'MMM d, yyyy'); } catch { return v as string; }
}

export function SeriesList() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [hideArchived, setHideArchived] = useState(true);

  const { data, isLoading, error } = useQuery({
    queryKey: ['catalog-series'],
    queryFn: catalog.series,
    staleTime: 5 * 60 * 1000,
  });

  const { data: publishersData } = useQuery({
    queryKey: ['catalog-publishers'],
    queryFn: catalog.publishers,
    staleTime: 10 * 60 * 1000,
  });

  const publisherMap = useMemo(
    () => new Map((publishersData?.publishers ?? []).map(p => [p._id, p.value || p.key])),
    [publishersData],
  );

  const all = data?.series ?? [];
  const filtered = useMemo(() => {
    let rows = all;
    if (hideArchived) rows = rows.filter(s => !s.isArchived);
    if (search) rows = rows.filter(s => s.name.toLowerCase().includes(search.toLowerCase()));
    return rows;
  }, [all, search, hideArchived]);

  return (
    <div className="p-8 max-w-6xl mx-auto">
      <div className="mb-6 flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Layers size={22} className="text-brand-600" /> Series
          </h1>
          <p className="text-gray-500 text-sm mt-1">{filtered.length} of {all.length} series</p>
        </div>
      </div>

      <div className="flex gap-3 mb-4 flex-wrap items-center">
        <div className="relative flex-1 min-w-48">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search series…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
          />
        </div>
        <label className="flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm text-gray-700 cursor-pointer hover:border-gray-300 whitespace-nowrap">
          <input
            type="checkbox"
            checked={hideArchived}
            onChange={e => setHideArchived(e.target.checked)}
            className="rounded border-gray-300 text-brand-600 focus:ring-brand-500"
          />
          Hide archived
        </label>
      </div>

      <Card>
        {isLoading ? (
          <div className="flex items-center justify-center py-16 text-gray-400">
            <Loader2 className="animate-spin mr-2" /> Loading…
          </div>
        ) : error ? (
          <div className="flex items-center justify-center py-16 text-red-500 gap-2 text-sm">
            <AlertCircle size={16} /> Failed to load series
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50">
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Series</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Release</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Publishers</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Items</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Groups</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filtered.map(s => {
                  const pubNames = Array.isArray(s.publisherIds)
                    ? s.publisherIds.map(pid => publisherMap.get(pid) ?? '').filter(Boolean).join(', ')
                    : '';
                  return (
                    <tr
                      key={s._id}
                      onClick={() => navigate(`/series/${s._id}`)}
                      className="hover:bg-gray-50 cursor-pointer transition-colors"
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          {imgUrl((s as any).image) ? (
                            <img
                              src={imgUrl((s as any).image)!}
                              alt=""
                              className="w-10 h-10 rounded object-cover bg-gray-100 flex-shrink-0"
                              onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
                            />
                          ) : (
                            <div className="w-10 h-10 rounded bg-gray-100 flex-shrink-0 flex items-center justify-center">
                              <Layers size={14} className="text-gray-400" />
                            </div>
                          )}
                          <div>
                            <span className="font-medium text-gray-900">{s.name}</span>
                            {s.slug && <p className="text-xs text-gray-400 font-mono mt-0.5">{s.slug}</p>}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {s.status ? (
                          <Badge variant={statusBadge(s.status)}>{s.status}</Badge>
                        ) : '—'}
                      </td>
                      <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">{fmtDate(s.releaseAt)}</td>
                      <td className="px-4 py-3 text-gray-500 text-xs max-w-[12rem] truncate">{pubNames || '—'}</td>
                      <td className="px-4 py-3 text-gray-600 text-xs text-right font-medium tabular-nums">{s.itemCount ?? '—'}</td>
                      <td className="px-4 py-3 text-gray-600 text-xs text-right font-medium tabular-nums">{s.groupCount ?? '—'}</td>
                    </tr>
                  );
                })}
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-5 py-12 text-center text-gray-400">
                      {search ? 'No series match the search' : 'No series found — try syncing the catalog'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
